import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard, Music, Building2, FolderOpen, Camera,
  LogOut, Check, X, Clock, Eye, AlertCircle,
  ChevronRight, ChevronLeft
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Textarea } from '../../components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status Badge Component
const StatusBadge = ({ status }) => {
  const config = {
    draft: { bg: 'bg-gray-500/20', text: 'text-gray-400', label: 'Borrador' },
    pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Pendiente' },
    approved: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Aprobado' },
    rejected: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Rechazado' },
    changes_requested: { bg: 'bg-orange-500/20', text: 'text-orange-400', label: 'Cambios Solicitados' },
  };
  const c = config[status] || config.draft;
  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
      {c.label}
    </span>
  );
};

// Sidebar Component
const Sidebar = ({ activeTab, setActiveTab, pendingCount }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const menuItems = [
    { id: 'queue', icon: Clock, label: 'Cola de Revisión', badge: pendingCount },
    { id: 'artists', icon: Music, label: 'Artistas' },
    { id: 'venues', icon: Building2, label: 'Venues' },
    { id: 'projects', icon: FolderOpen, label: 'Proyectos' },
    { id: 'models', icon: Camera, label: 'Modelos' },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <aside className="fixed left-0 top-0 bottom-0 w-64 bg-[#0A0A0A] border-r border-white/5 flex flex-col z-40">
      <div className="p-6 border-b border-white/5">
        <Link to="/" className="flex items-center gap-2">
          <img src="/gs-agency-logo.png" alt="GS Agency" className="h-10 w-auto object-contain" />
        </Link>
      </div>

      <nav className="flex-1 p-4 overflow-y-auto">
        <p className="text-cyan-400 text-xs uppercase tracking-wider px-4 mb-4 flex items-center gap-2">
          <Eye size={12} /> Panel Curator
        </p>
        {menuItems.map((item) => (
          <button
            key={item.id}
            onClick={() => setActiveTab(item.id)}
            className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-lg text-sm transition-all mb-1 ${
              activeTab === item.id
                ? 'bg-cyan-500/10 text-cyan-400'
                : 'text-white/60 hover:text-white hover:bg-white/5'
            }`}
          >
            <div className="flex items-center gap-3">
              <item.icon size={18} />
              {item.label}
            </div>
            {item.badge > 0 && (
              <span className="bg-yellow-500 text-black text-xs font-bold px-2 py-0.5 rounded-full">
                {item.badge}
              </span>
            )}
          </button>
        ))}
      </nav>

      <div className="p-4 border-t border-white/5">
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white/5">
          <div className="w-10 h-10 rounded-full bg-cyan-500/20 flex items-center justify-center">
            <Eye size={18} className="text-cyan-400" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-medium truncate">{user?.name}</p>
            <p className="text-white/40 text-xs">Curator</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-400 hover:bg-red-500/10 mt-2 text-sm transition-colors"
        >
          <LogOut size={18} />
          Cerrar Sesión
        </button>
      </div>
    </aside>
  );
};

// Review Queue Tab
const ReviewQueueTab = ({ token, onUpdateCount }) => {
  const [pending, setPending] = useState({ artists: [], venues: [], projects: [], models: [] });
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchPending = async () => {
    try {
      const response = await axios.get(`${API}/admin/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPending(response.data);
      const total = (response.data.artists?.length || 0) + 
                   (response.data.venues?.length || 0) + 
                   (response.data.projects?.length || 0) +
                   (response.data.models?.length || 0);
      onUpdateCount(total);
    } catch (error) {
      console.error('Error fetching pending:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPending(); }, [token]);

  const allPending = [
    ...pending.artists.map(i => ({ ...i, type: 'artist', idField: 'artist_id', nameField: 'name', icon: Music })),
    ...pending.venues.map(i => ({ ...i, type: 'venue', idField: 'venue_id', nameField: 'name', icon: Building2 })),
    ...pending.projects.map(i => ({ ...i, type: 'project', idField: 'project_id', nameField: 'title', icon: FolderOpen })),
    ...(pending.models || []).map(i => ({ ...i, type: 'model', idField: 'model_id', nameField: 'name', icon: Camera })),
  ];

  const reviewContent = async (status) => {
    if (!selectedItem) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/review/${selectedItem.type}/${selectedItem[selectedItem.idField]}`, {
        status,
        notes: reviewNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const messages = {
        approved: '✅ Contenido aprobado y publicado',
        rejected: '❌ Contenido rechazado',
        changes_requested: '📝 Cambios solicitados'
      };
      toast.success(messages[status]);
      
      setSelectedItem(null);
      setReviewNotes('');
      fetchPending();
    } catch (error) {
      toast.error('Error al revisar contenido');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (allPending.length === 0) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="font-display text-3xl text-white mb-2">Cola de Revisión</h1>
          <p className="text-white/50">Contenido pendiente de aprobación</p>
        </div>
        
        <div className="glass-dark rounded-2xl p-16 text-center border border-white/5">
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <Check size={40} className="text-green-400" />
          </div>
          <h2 className="text-white font-display text-2xl mb-3">¡Cola vacía!</h2>
          <p className="text-white/50 max-w-md mx-auto">
            No hay contenido pendiente de revisión en este momento. 
            Todos los envíos han sido procesados.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl text-white mb-2">Cola de Revisión</h1>
          <p className="text-white/50">{allPending.length} elementos pendientes</p>
        </div>
      </div>

      {/* Two-column layout: list + preview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pending Items List */}
        <div className="glass-dark rounded-xl border border-white/5 overflow-hidden">
          <div className="p-4 border-b border-white/5">
            <h2 className="text-white font-medium">Pendientes de Revisión</h2>
          </div>
          <div className="max-h-[600px] overflow-y-auto">
            {allPending.map((item, idx) => {
              const name = item[item.nameField]?.es || item[item.nameField]?.en || 'Sin nombre';
              const Icon = item.icon;
              const isSelected = selectedItem?.[item.idField] === item[item.idField];
              
              return (
                <button
                  key={`${item.type}-${item[item.idField]}`}
                  onClick={() => setSelectedItem(item)}
                  className={`w-full flex items-center gap-4 p-4 border-b border-white/5 transition-colors text-left ${
                    isSelected ? 'bg-cyan-500/10' : 'hover:bg-white/5'
                  }`}
                >
                  {item.cover_image ? (
                    <img src={item.cover_image} alt={name} className="w-16 h-16 rounded-lg object-cover" />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-white/10 flex items-center justify-center">
                      <Icon size={24} className="text-white/30" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{name}</p>
                    <p className="text-white/40 text-sm capitalize">{item.type} • {item.city || item.category || ''}</p>
                  </div>
                  <ChevronRight size={20} className={`text-white/30 ${isSelected ? 'text-cyan-400' : ''}`} />
                </button>
              );
            })}
          </div>
        </div>

        {/* Preview & Review Panel */}
        <div className="glass-dark rounded-xl border border-white/5 overflow-hidden">
          {selectedItem ? (
            <>
              {/* Preview Header */}
              <div className="aspect-video relative bg-white/5">
                {selectedItem.cover_image ? (
                  <img 
                    src={selectedItem.cover_image} 
                    alt="" 
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <selectedItem.icon size={64} className="text-white/10" />
                  </div>
                )}
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-500 text-black">
                    {selectedItem.type.toUpperCase()}
                  </span>
                </div>
              </div>

              {/* Content Details */}
              <div className="p-6">
                <h3 className="text-white font-display text-2xl mb-2">
                  {selectedItem[selectedItem.nameField]?.es || selectedItem[selectedItem.nameField]?.en}
                </h3>
                <p className="text-white/50 text-sm mb-4">
                  {selectedItem.city && `📍 ${selectedItem.city}`}
                  {selectedItem.category && ` • 📂 ${selectedItem.category}`}
                </p>
                
                {(selectedItem.bio?.es || selectedItem.description?.es || selectedItem.summary?.es) && (
                  <div className="mb-6">
                    <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Descripción</p>
                    <p className="text-white/70 text-sm line-clamp-4">
                      {selectedItem.bio?.es || selectedItem.description?.es || selectedItem.summary?.es}
                    </p>
                  </div>
                )}

                {/* Gallery Preview */}
                {selectedItem.gallery?.length > 0 && (
                  <div className="mb-6">
                    <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Galería ({selectedItem.gallery.length} imágenes)</p>
                    <div className="flex gap-2 overflow-x-auto pb-2">
                      {selectedItem.gallery.slice(0, 4).map((img, idx) => (
                        <img key={idx} src={img} alt="" className="w-16 h-16 rounded object-cover flex-shrink-0" />
                      ))}
                    </div>
                  </div>
                )}

                {/* Review Notes */}
                <div className="mb-6">
                  <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Comentarios (opcional para aprobar)</p>
                  <Textarea
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    placeholder="Escribe un comentario para el usuario..."
                    className="input-premium text-sm"
                    rows={3}
                  />
                </div>

                {/* Action Buttons */}
                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => reviewContent('approved')}
                    disabled={submitting}
                    className="bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    <Check size={18} />
                    Aprobar
                  </button>
                  <button
                    onClick={() => reviewContent('changes_requested')}
                    disabled={submitting || !reviewNotes.trim()}
                    className="bg-yellow-500 hover:bg-yellow-600 disabled:opacity-50 text-black py-3 rounded-lg font-medium transition-colors"
                  >
                    Cambios
                  </button>
                  <button
                    onClick={() => reviewContent('rejected')}
                    disabled={submitting || !reviewNotes.trim()}
                    className="bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    <X size={18} />
                    Rechazar
                  </button>
                </div>

                {!reviewNotes.trim() && (
                  <p className="text-white/40 text-xs text-center mt-3">
                    💡 Para rechazar o solicitar cambios, debes agregar un comentario
                  </p>
                )}
              </div>
            </>
          ) : (
            <div className="h-full flex flex-col items-center justify-center p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mb-4">
                <ChevronLeft size={32} className="text-white/20" />
              </div>
              <h3 className="text-white/50 font-medium mb-2">Selecciona un elemento</h3>
              <p className="text-white/30 text-sm">
                Haz clic en un elemento de la lista para ver su contenido y revisarlo
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Content Type Tab (for viewing already reviewed content)
const ContentTypeTab = ({ type, token }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const typeConfig = {
    artists: { label: 'Artistas', icon: Music, idField: 'artist_id', nameField: 'name' },
    venues: { label: 'Venues', icon: Building2, idField: 'venue_id', nameField: 'name' },
    projects: { label: 'Proyectos', icon: FolderOpen, idField: 'project_id', nameField: 'title' },
    models: { label: 'Modelos', icon: Camera, idField: 'model_id', nameField: 'name' },
  };

  const config = typeConfig[type];

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const response = await axios.get(`${API}/admin/${type}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setItems(response.data.items || []);
      } catch (error) {
        console.error(`Error fetching ${type}:`, error);
      } finally {
        setLoading(false);
      }
    };
    fetchItems();
  }, [type, token]);

  const filteredItems = filter === 'all' ? items : items.filter(i => i.status === filter);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl text-white mb-2">{config.label}</h1>
          <p className="text-white/50">{items.length} registros totales</p>
        </div>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="input-premium"
        >
          <option value="all">Todos</option>
          <option value="draft">Borrador</option>
          <option value="pending">Pendiente</option>
          <option value="approved">Aprobado</option>
          <option value="rejected">Rechazado</option>
          <option value="changes_requested">Cambios</option>
        </select>
      </div>

      <div className="glass-dark rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left text-white/50 text-xs uppercase tracking-wider p-4">Contenido</th>
              <th className="text-left text-white/50 text-xs uppercase tracking-wider p-4">Ciudad/Categoría</th>
              <th className="text-left text-white/50 text-xs uppercase tracking-wider p-4">Estado</th>
              <th className="text-left text-white/50 text-xs uppercase tracking-wider p-4">Última actualización</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.map((item) => {
              const name = item[config.nameField]?.es || item[config.nameField]?.en || 'Sin nombre';
              return (
                <tr key={item[config.idField]} className="border-b border-white/5 hover:bg-white/5">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      {item.cover_image ? (
                        <img src={item.cover_image} alt={name} className="w-12 h-12 rounded object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded bg-white/10 flex items-center justify-center">
                          <config.icon size={20} className="text-white/30" />
                        </div>
                      )}
                      <span className="text-white">{name}</span>
                    </div>
                  </td>
                  <td className="p-4 text-white/70">{item.city || item.category || '-'}</td>
                  <td className="p-4"><StatusBadge status={item.status} /></td>
                  <td className="p-4 text-white/50 text-sm">
                    {item.updated_at ? new Date(item.updated_at).toLocaleDateString() : '-'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {filteredItems.length === 0 && (
          <div className="p-12 text-center text-white/50">
            No hay {config.label.toLowerCase()} con este filtro
          </div>
        )}
      </div>
    </div>
  );
};

// Main CuratorDashboard Component
export default function CuratorDashboard() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('queue');
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    if (!user || !['curator', 'admin', 'super_admin'].includes(user.role)) {
      navigate('/login');
    }
  }, [user, navigate]);

  const renderContent = () => {
    switch (activeTab) {
      case 'queue':
        return <ReviewQueueTab token={token} onUpdateCount={setPendingCount} />;
      case 'artists':
        return <ContentTypeTab type="artists" token={token} />;
      case 'venues':
        return <ContentTypeTab type="venues" token={token} />;
      case 'projects':
        return <ContentTypeTab type="projects" token={token} />;
      case 'models':
        return <ContentTypeTab type="models" token={token} />;
      default:
        return <ReviewQueueTab token={token} onUpdateCount={setPendingCount} />;
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Sidebar activeTab={activeTab} setActiveTab={setActiveTab} pendingCount={pendingCount} />
      
      <main className="ml-64 p-8">
        {renderContent()}
      </main>
    </div>
  );
}
